import { FormDataType } from 'contexts/FormProvider';
import { CustomerType } from 'pages/Complaint/InsuredDetails/enum';
import { formatDate, toNumber } from '../utils/utils';
import getOr from 'lodash/get';
import isEmpty from 'lodash/fp/isEmpty';

import { remapFHA } from './remap_fha';

import {
  ClaimCategoryList,
  FinancialHardshipCategoryList,
  SalesCategoryList,
} from 'pages/Complaint/ComplaintDetailsForm/enum';

import { TextValueType } from './constants';

const getComplaintCategory = (compType: string, compCategory: string[]) => {
  let categoryList: TextValueType[] = [];
  if (compType === 'sales') {
    categoryList = SalesCategoryList;
  }
  if (compType === 'claims') {
    categoryList = ClaimCategoryList;
  }
  if (compType === 'financialHardshipApp') {
    categoryList = FinancialHardshipCategoryList;
  }

  return categoryList.filter((cat) => compCategory.includes(cat.value)).map((cat) => cat.text);
};

export const remap = (data: FormDataType): object => {
  const isIndividual = data.insuredIndOrBus === CustomerType.INDIVIDUAL;
  const isFHA = data.lodgement === 'fha';

  const get = (key: keyof FormDataType, defaultValue: string | null = null) => {
    if (isEmpty(data)) {
      return null;
    }
    return getOr(data, key, defaultValue);
  };

  if (isFHA) return remapFHA(data);

  const postalAddressContact =
    data.contactPreferedMethod === 'post'
      ? {
          country: get('country'),
          street_address: get('streetAddress'),
          suburb: get('suburb'),
          state: get('state'),
          postcode: get('postcode'),
        }
      : null;

  const insuredPersonalDetails = isIndividual
    ? {
        title: data.customerTitle,
        first_name: data.customerFirstName,
        last_name: data.customerLastName,
        date_of_birth: formatDate(data.dob),
        gender: data.gender,
      }
    : {
        title: data?.contactPersonTitle,
        first_name: data.contactFirstName,
        last_name: data.contactLastName,
        date_of_birth: null,
        gender: null,
      };

  let insuredPostalAddress = null;
  if (data.addressType === 'no') {
    insuredPostalAddress = {
      country: get('pCountry'),
      street_address: get('pStreetAddress'),
      suburb: get('pSuburb'),
      state: get('pState'),
      postcode: get('pPostcode'),
    };
  }

  return {
    your_details: {
      policy_or_claim: {
        contact_reason: data.policyOrClaim,
        claim_number: get('claimNumber'),
        policy_number: get('policyNumber'),
      },
      personal_details: {
        relationship: data.relationship,
        first_name: data.firstName,
        last_name: data.lastName,
        organisation: data.organisation,
      },
      contact_details: {
        email_address: data.emailAddress,
        phone_number: data.phoneNumber,
        provider_preferred_contact_person: data.contactType,
        provider_preferred_contact_method: data.contactPreferedMethod,
        postal_address: postalAddressContact,
      },
    },
    insured_details: {
      insured_details: {
        individual_or_business: data.insuredIndOrBus,
        ...insuredPersonalDetails,
      },
      contact_details: {
        // ConsentForm
        preferred_method_of_contact: data.iContactPreferedMethod,
        email_address: data.cEmailAddress,
        physical_address: {
          country: get('rCountry'),
          street_address: get('rStreetAddress'),
          suburb: get('rSuburb'),
          state: get('rState'),
          postcode: get('rPostcode'),
        },
        postal_address_is_same_as_physical_address: data.addressType,
        postal_address: insuredPostalAddress,

        mobile_phone: data?.mPhoneNumber,
        home_phone: data?.hPhoneNumber,
        other_phone: data?.oPhoneNumber,
      },
      other_details: {
        vulnerable_customer: data.isVulnerableCustomer,
        reason_for_vulnerability: data.isVulnerableCustomer === 'yes' ? data.vulnerabilityReason : null,
        language_interpreter_required: data.isLanguageInterpreter || null,
        language: data?.language || null,
        other_language: data?.otherLanguage || null,
        important_text: get('customerDesc'),
        business_name: data?.businessName || null,
        small_business: data?.isSmallBusiness || null,
      },
    },
    tell_us_more: {
      complaint_details: {
        date_of_complaint: data.complaintDate ? formatDate(data.complaintDate) : null,
        source_of_complaint: data.sourceOfComplaint === 'Online form' ? 'Webform' : data.sourceOfComplaint,
        acknowledgement: data.acknowledgement === 'yes' ? 'True' : 'False', // JSON to have True/False
        acknowledgement_date: data.acknowledgementDate ? formatDate(data.acknowledgementDate) : null,
        product: data.productType,
        complaint_description: get('compDescription'),
        complaint_type: data.compType,
        complaint_category: data.compType ? getComplaintCategory(data.compType, data.compCategory) : null,
        sensitive_case: data.sensitiveCase,
        resolved: data.resolved,
        desired_outcome: get('desiredOutcome'),
        date_of_resolution: data.resolvedDate ? formatDate(data.resolvedDate) : null, // date
        outcome: data.outcomeType,
        outcome_description: get('outcomeDesc'),
        amount_in_dispute: toNumber(data.amountInDispute),
        amount_complaint_resolved: data.resolved === 'yes' ? toNumber(get('amountCompResolved') as string) : null,
      },
    },
  };
};
